import { Area, AreaChart, CartesianGrid, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { AlertTriangle, ArrowDownRight, ArrowUpRight, Lightbulb, TrendingUp } from "lucide-react";
import { inr, num, pct, title } from "../lib/format";
import { Eyebrow, KpiTile, StatusBadge, SectionTitle, Facet } from "./kit";

function Hero({ d }) {
  const s = d.expectation.sales;
  const ahead = s.gap >= 0;
  const progress = Math.min(100, s.achievement);
  const expPos = s.target ? Math.min(100, (s.expected / s.target) * 100) : 0;
  return (
    <section className="bs-navy-panel rounded-3xl p-6 bs-rise" data-testid="my-sales-hero">
      <div className="flex items-center justify-between">
        <Eyebrow className="!text-white/60">{d.basis === "ex_coin" ? "My Net Sales · w/o Gold Coin" : "My Net Sales"}</Eyebrow>
        <StatusBadge status={d.status} testid="my-status-badge" large />
      </div>
      <div className="num text-5xl sm:text-6xl font-extrabold mt-3" data-testid="my-sales-value">{inr(s.actual)}</div>
      <div className="grid grid-cols-2 gap-4 mt-5 text-sm">
        <div><div className="text-white/50">Target</div><div className="num text-xl font-semibold" data-testid="my-sales-target">{inr(s.target)}</div></div>
        <div><div className="text-white/50">Expected</div><div className="num text-xl font-semibold" data-testid="my-sales-expected">{inr(s.expected)}</div></div>
      </div>
      <div className="relative h-2 rounded-full bg-white/15 mt-5">
        <div className="absolute inset-y-0 left-0 rounded-full bg-white" style={{ width: `${progress}%` }} />
        <div className="absolute -top-1 w-0.5 h-4 bg-[#C5A059]" style={{ left: `${expPos}%` }} title="Expected" />
      </div>
      <div className="text-xs text-white/50 mt-2">{pct(s.achievement)} achieved · gold mark = expected today</div>
      <div className="grid grid-cols-1 gap-3 mt-6">
        <div className={`rounded-2xl p-4 ${ahead ? "bg-green-500/15" : "bg-orange-500/15"}`}>
          <div className="num text-3xl font-bold flex items-center gap-2" data-testid="my-sales-gap">
            {ahead ? <ArrowUpRight className="w-7 h-7 text-green-300" /> : <ArrowDownRight className="w-7 h-7 text-orange-300" />}
            {inr(Math.abs(s.gap))} {ahead ? "Ahead" : "Behind"}
          </div>
        </div>
        <div className="rounded-2xl p-4 bg-white text-[#011E38]">
          <div className="num text-3xl font-bold" data-testid="my-required-per-day">{inr(s.required_daily)}/day <span className="text-base font-semibold text-slate-500">Required</span></div>
        </div>
      </div>
    </section>
  );
}

function Today({ d, cats }) {
  const t = d.today;
  return (
    <section className="bs-card p-6 border-[#C5A059]/40 bs-rise" data-testid="today-target-section">
      <Eyebrow className="!text-[#C5A059] flex items-center gap-2"><Facet className="w-3.5 h-3.5" />What I need to achieve today</Eyebrow>
      <div className="mt-4 text-sm text-slate-500">Today's Required Sales</div>
      <div className="num text-5xl font-extrabold text-[#011E38]" data-testid="today-required-sales">{inr(t.required_sales)}</div>
      <div className="grid grid-cols-2 gap-3 mt-5">
        <div className="rounded-xl bg-slate-50 p-3"><div className="text-xs text-slate-500">Normal Target</div><div className="num text-xl font-bold text-[#011E38]" data-testid="today-normal-sales">{inr(t.normal_sales)}</div></div>
        <div className="rounded-xl bg-orange-50 p-3"><div className="text-xs text-orange-700">Additional Required</div><div className="num text-xl font-bold text-orange-700" data-testid="today-additional-sales">{inr(t.additional_sales)}</div></div>
        <div className="rounded-xl bg-slate-50 p-3"><div className="text-xs text-slate-500">Today's GMS</div><div className="num text-xl font-bold text-[#011E38]" data-testid="today-required-gms">{inr(t.required_gms)}</div></div>
        <div className="rounded-xl bg-slate-50 p-3"><div className="text-xs text-slate-500">Remaining Target</div><div className="num text-xl font-bold text-[#011E38]" data-testid="today-remaining-target">{inr(t.remaining_target)}</div></div>
        <div className="rounded-xl bg-slate-50 p-3"><div className="text-xs text-slate-500">Working Days Left</div><div className="num text-xl font-bold text-[#011E38]" data-testid="today-remaining-days">{t.remaining_days}</div></div>
        <div className="rounded-xl bg-slate-50 p-3"><div className="text-xs text-slate-500">Gap to Recover</div><div className="num text-xl font-bold text-[#011E38]" data-testid="today-sales-gap">{inr(t.sales_gap)}</div></div>
      </div>
      <div className="mt-5">
        <div className="text-xs text-slate-500 mb-2">Today's Category Requirement</div>
        <div className="grid grid-cols-3 gap-2">
          {cats.map((c) => (
            <div key={c.key} className="rounded-xl border border-slate-200 p-3 text-center" data-testid={`today-cat-${c.key}`}>
              <div className="text-xs text-slate-500">{c.name}</div>
              <div className="num text-lg font-bold text-[#011E38]">{inr(t.cats[c.key])}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Diagnostic({ diag, testid = "diagnostic-card" }) {
  const b = diag.biggest;
  if (!b)
    return (
      <section className="bs-card p-6 bg-green-50/60 border-green-200" data-testid={testid}>
        <Eyebrow>Biggest gap</Eyebrow>
        <div className="font-display text-2xl font-bold text-green-800 mt-2 flex items-center gap-2"><TrendingUp /> No major gap. Keep the momentum.</div>
      </section>
    );
  return (
    <section className="bs-card p-6 bs-rise" data-testid={testid}>
      <Eyebrow className="flex items-center gap-2 !text-red-700"><AlertTriangle className="w-3.5 h-3.5" /> Biggest gap</Eyebrow>
      <div className="font-display text-2xl sm:text-3xl font-bold text-[#011E38] mt-2" data-testid="diagnostic-biggest">{b.title}</div>
      <div className="mt-4 grid gap-3">
        <div><div className="bs-eyebrow">Why</div><div className="text-slate-700 mt-1" data-testid="diagnostic-why">{b.why}</div></div>
        <div className="rounded-xl bg-[#FAF6EE] p-4 flex gap-3">
          <Lightbulb className="w-5 h-5 text-[#C5A059] shrink-0 mt-0.5" />
          <div><div className="bs-eyebrow !text-[#8a6d2f]">What to improve</div><div className="text-slate-800 mt-1" data-testid="diagnostic-improve">{b.improve}</div></div>
        </div>
        {diag.secondary && (
          <div className="border-t border-slate-100 pt-3">
            <div className="bs-eyebrow">Secondary gap</div>
            <div className="font-semibold text-[#011E38] mt-1" data-testid="diagnostic-secondary">{diag.secondary.title}</div>
            <div className="text-sm text-slate-500">{diag.secondary.why}</div>
          </div>
        )}
      </div>
    </section>
  );
}

function KpiGroups({ d, cats }) {
  const k = d.kpis;
  const st = d.kpi_status;
  const nm = (key) => cats.find((c) => c.key === key)?.name || key;
  const qtyKeys = cats.map((c) => c.key);
  return (
    <section data-testid="kpi-summary" className="space-y-5">
      <div className="bs-card p-5" data-testid="sales-net-card">
        <div className="bs-eyebrow">Sales</div>
        <div className="text-xs text-slate-500 mt-2">Net Sales</div>
        <div className="num text-4xl font-extrabold text-[#011E38]" data-testid="kpi-net-sales-value">{inr(k.net_sales)}</div>
        <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
          <div><div className="text-slate-500 text-xs">Gross Sales</div><div className="num text-lg font-bold text-[#011E38]" data-testid="kpi-gross-sales-value">{inr(k.gross_sales)}</div></div>
          <div><div className="text-slate-500 text-xs">Returns</div><div className="num text-lg font-bold text-red-700" data-testid="kpi-returns-value">-{inr(k.return_sales)}</div></div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2.5" data-testid="sales-mix">
        <KpiTile label="Without Gold Coin" value={inr(k.sales_ex_coin)} testid="kpi-sales-ex-coin" />
        <KpiTile label="Gold Coin" value={inr(k.gold_coin)} testid="kpi-gold-coin-sales" />
      </div>
      <div className="bs-card p-5" data-testid="products-card">
        <div className="bs-eyebrow">Products</div>
        <div className="flex items-end justify-between mt-2">
          <div><div className="text-xs text-slate-500">Net Products</div><div className="num text-4xl font-extrabold text-[#011E38]" data-testid="kpi-net-products-value">{num(k.units)}</div></div>
          <div className="text-right text-xs text-slate-500">Gross {num(k.gross_units)}<br />Returned <span className="text-red-700 font-semibold">-{num(k.return_units)}</span></div>
        </div>
        <div className="grid grid-cols-4 gap-2 mt-4">
          {qtyKeys.map((q) => (
            <div key={q} className="rounded-xl bg-slate-50 p-2.5 text-center" data-testid={`kpi-qty-${q}`}>
              <div className="text-[0.65rem] text-slate-500 leading-tight">{nm(q)}</div>
              <div className="num text-xl font-bold text-[#011E38]">{num(k.qty?.[q])}</div>
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="bs-eyebrow mb-2">Customer</div>
        <div className="grid grid-cols-3 gap-2.5">
          <KpiTile label="Walk-ins" value={num(k.walkins)} status={st.walkins} testid="kpi-walk-ins" />
          <KpiTile label="Bills" value={num(k.bills)} status={st.bills} testid="kpi-bills" />
          <KpiTile label="Conv" value={pct(k.conversion)} status={st.conversion} testid="kpi-conversion" />
        </div>
      </div>
      <div>
        <div className="bs-eyebrow mb-2">Performance</div>
        <div className="grid grid-cols-2 gap-2.5">
          <KpiTile label="ATV" value={inr(k.atv)} status={st.atv} testid="kpi-atv" />
          <KpiTile label="ASP" value={inr(k.asp)} status={st.asp} testid="kpi-asp" />
          <KpiTile label="UPT" value={num(k.upt, 2)} status={st.upt} testid="kpi-upt" />
          <KpiTile label="GMS / Plan" value={inr(k.gms)} status={st.gms} sub={`${pct(k.gms_pct)} of net`} testid="kpi-gms" />
        </div>
      </div>
      <div>
        <div className="bs-eyebrow mb-2">Category (net)</div>
        <div className="grid grid-cols-2 gap-2.5">
          {cats.map((c) => <KpiTile key={c.key} label={c.name} value={inr(k.cats[c.key])} status={st[c.key]} sub={`${pct(k.cat_pct[c.key])} mix`} testid={`kpi-cat-${c.key}`} />)}
        </div>
      </div>
    </section>
  );
}

function Availability({ a }) {
  if (!a) return null;
  const items = [["Store working days", a.store_days], ["Weekly off", a.weekly_off], ["Week off (marked)", a.week_off], ["Approved leave", a.leave]];
  return (
    <section className="bs-card p-5" data-testid="availability-card">
      <SectionTitle eyebrow="Attendance" title="Days available for sale" />
      <div className="flex items-end gap-3">
        <div className="num font-display text-5xl font-extrabold text-[#011E38]" data-testid="availability-available">{a.available}</div>
        <div className="text-slate-500 pb-1.5">of {a.store_days} days · {a.available_so_far} available so far</div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-sm">
        {items.map(([l, v]) => <div key={l} className="rounded-xl bg-slate-50 px-3 py-2"><div className="text-xs text-slate-500">{l}</div><div className="num font-bold text-lg text-[#011E38]">{v}</div></div>)}
      </div>
      {a.leave_pending > 0 && <div className="text-xs text-amber-700 mt-3" data-testid="availability-pending">{a.leave_pending} leave day(s) awaiting manager approval — still counted as available.</div>}
    </section>
  );
}

function Recovery({ d, cats }) {
  const rows = [["Sales", d.expectation.sales], ["GMS", d.expectation.gms], ...cats.map((c) => [c.name, d.cat_expectation[c.key]])];
  return (
    <section data-testid="recovery-calculator">
      <SectionTitle eyebrow="Recovery calculator" title={`${d.days.remaining} working days left`} />
      <div className="space-y-2.5">
        {rows.map(([l, e]) => (
          <div key={l} className="bs-card p-4" data-testid={`recovery-${l.toLowerCase()}`}>
            <div className="flex items-center justify-between">
              <div className="font-display font-semibold text-[#011E38] text-lg">{l}</div>
              <StatusBadge status={e.status} />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 text-sm">
              <div><div className="text-slate-500 text-xs">Gap</div><div className={`num font-bold text-lg ${e.gap < 0 ? "text-orange-700" : "text-green-700"}`}>{inr(e.gap)}</div></div>
              <div><div className="text-slate-500 text-xs">Required / day</div><div className="num font-bold text-lg text-[#011E38]">{inr(e.required_daily)}</div></div>
              <div><div className="text-slate-500 text-xs">Normal / day</div><div className="num font-bold text-lg text-slate-700">{inr(e.normal_daily)}</div></div>
              <div><div className="text-slate-500 text-xs">Additional / day</div><div className="num font-bold text-lg text-slate-700">{inr(e.additional_daily)}</div></div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function VsStore({ d, cats }) {
  const m = d.kpis;
  const s = d.store.kpis;
  const rows = [
    ["Conversion", pct(m.conversion), pct(s.conversion), m.conversion, s.conversion],
    ["ATV", inr(m.atv), inr(s.atv), m.atv, s.atv],
    ["UPT", num(m.upt, 2), num(s.upt, 2), m.upt, s.upt],
    ["ASP", inr(m.asp), inr(s.asp), m.asp, s.asp],
    ["GMS %", pct(m.gms_pct), pct(s.gms_pct), m.gms_pct, s.gms_pct],
    ...cats.map((c) => [`${c.name} %`, pct(m.cat_pct[c.key]), pct(s.cat_pct[c.key]), m.cat_pct[c.key], s.cat_pct[c.key]]),
  ];
  return (
    <section data-testid="my-vs-store">
      <SectionTitle eyebrow="Context" title="My KPI vs Store KPI" />
      <div className="bs-card divide-y divide-slate-100">
        {rows.map(([l, mv, sv, a, b]) => (
          <div key={l} className="grid grid-cols-3 items-center px-4 py-3.5" data-testid={`vs-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="text-sm text-slate-500">{l}</div>
            <div className={`num text-xl font-bold ${a >= b ? "text-green-700" : "text-[#011E38]"}`}>{mv}<div className="text-[0.65rem] font-semibold text-slate-400 uppercase tracking-wider">Me</div></div>
            <div className="num text-xl font-bold text-slate-500">{sv}<div className="text-[0.65rem] font-semibold text-slate-400 uppercase tracking-wider">Store</div></div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function StoreCurrent({ k, cats, testid = "store-current-performance" }) {
  const items = [
    ["Gross Sales", inr(k.gross_sales)], ["Returns", `-${inr(k.return_sales)}`], ["Net Sales", inr(k.net_sales)],
    ["W/o Gold Coin", inr(k.sales_ex_coin)], ["Gold Coin", inr(k.gold_coin)], ["GMS / Plan", inr(k.gms)],
    ["Walk-ins", num(k.walkins)], ["Bills", num(k.bills)], ["Conversion", pct(k.conversion)], ["ATV", inr(k.atv)],
    ["ASP", inr(k.asp)], ["UPT", num(k.upt, 2)], ["Gross Products", num(k.gross_units)], ["Returned Products", `-${num(k.return_units)}`],
    ["Net Products", num(k.units)],
    ...cats.filter((c) => c.key !== "gold_coin").map((c) => [c.name, inr(k.cats[c.key])]),
    ...cats.map((c) => [`${c.name} Qty`, num(k.qty?.[c.key])]),
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5" data-testid={testid}>
      {items.map(([l, v]) => <KpiTile key={l} label={l} value={v} testid={`${testid}-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`} />)}
    </div>
  );
}

export function PaceChart({ series, height = 220 }) {
  if (!series?.length) return null;
  return (
    <div style={{ height }} data-testid="pace-chart">
      <ResponsiveContainer>
        <AreaChart data={series} margin={{ left: -10, right: 8, top: 8 }}>
          <defs>
            <linearGradient id="bsArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#011E38" stopOpacity={0.25} />
              <stop offset="100%" stopColor="#011E38" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#eef2f6" vertical={false} />
          <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
          <YAxis tickFormatter={inr} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={64} />
          <Tooltip formatter={(v, n) => [inr(v), title(n)]} labelFormatter={(l) => `Day ${l}`} />
          <Area type="monotone" dataKey="cumulative" stroke="#011E38" strokeWidth={2.5} fill="url(#bsArea)" />
          <Line type="monotone" dataKey="expected" stroke="#C5A059" strokeDasharray="5 4" strokeWidth={2} dot={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function StaffPerformanceView({ d, cats }) {
  return (
    <div className="space-y-8">
      <Hero d={d} />
      <Today d={d} cats={cats} />
      <Diagnostic diag={d.diagnostic} />
      <KpiGroups d={d} cats={cats} />
      <Availability a={d.availability} />
      <Recovery d={d} cats={cats} />
      <VsStore d={d} cats={cats} />
      <section>
        <SectionTitle eyebrow={d.store_info.name} title="Store Current Performance" right={<StatusBadge status={d.store.status} testid="store-status-badge" />} />
        <StoreCurrent k={d.store.kpis} cats={cats} />
      </section>
      <section className="bs-card p-5">
        <SectionTitle eyebrow="Month so far" title="My pace vs expected" />
        <PaceChart series={d.series} />
      </section>
      <div className="text-xs text-slate-400 text-center">
        {d.days.elapsed} of {d.days.total} working days done · as of {d.days.as_of}
      </div>
    </div>
  );
}

export { Diagnostic };
